import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../utils/cn';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  padded?: boolean;
}

export function Card({ title, subtitle, action, padded = true, className, children, ...rest }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-card)]',
        // Two-layer elevation — a tight 1 px offset for crispness +
        // a wider, softer outer halo so the card lifts visibly off
        // the near-white app background in light mode and still
        // reads as "raised surface" against dark navy in dark mode.
        'shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_24px_-6px_rgba(15,23,42,0.10)]',
        className
      )}
      {...rest}
    >
      {(title || action) && (
        <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] px-4 py-3">
          <div>
            {title && <div className="text-sm font-semibold text-[var(--color-text-primary)]">{title}</div>}
            {subtitle && <div className="text-xs text-[var(--color-text-secondary)]">{subtitle}</div>}
          </div>
          {action}
        </div>
      )}
      <div className={cn(padded && 'p-4')}>{children}</div>
    </div>
  );
}

export default Card;
